import { useState } from 'react';
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import type { StorePerfRow } from './storePerformanceModel';
import { formatRate } from './format';

/**
 * The per-band store leaderboard. Ranked stores (at or above the click floor)
 * sort by conversion rate ascending by default, so the worst-converting stores
 * with real traffic (the leak-hunt) sit on top; columns are sortable, so clicks
 * descending (the volume view) is one click away. Sub-floor stores are listed
 * separately below, UNRANKED, because their rate is too noisy to trust. Rows
 * below the band median rate are flagged so the leaks jump.
 */

type SortField = 'name' | 'clicks' | 'conversions' | 'conversionRate';

const COLUMNS: ReadonlyArray<{
  field: SortField;
  label: string;
  numeric: boolean;
}> = [
  { field: 'name', label: 'Store', numeric: false },
  { field: 'clicks', label: 'Clicks', numeric: true },
  { field: 'conversions', label: 'Conversions', numeric: true },
  { field: 'conversionRate', label: 'Conversion rate', numeric: true },
];

const compare = (
  a: StorePerfRow,
  b: StorePerfRow,
  field: SortField,
): number => {
  if (field === 'name') return a.name.localeCompare(b.name);
  const av = a[field];
  const bv = b[field];
  // A null rate (no matured clicks) always sorts last.
  if (av == null) return 1;
  if (bv == null) return -1;
  return (av as number) - (bv as number);
};

const StoreRow = ({
  row,
  belowMedian,
}: {
  row: StorePerfRow;
  belowMedian: boolean;
}) => (
  <TableRow>
    <TableCell>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
      >
        <span>{row.name}</span>
        {belowMedian && (
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label="below median"
          />
        )}
      </Box>
    </TableCell>
    <TableCell align="right">{row.clicks}</TableCell>
    <TableCell align="right">{row.conversions}</TableCell>
    <TableCell align="right">{formatRate(row.conversionRate)}</TableCell>
  </TableRow>
);

export const StorePerformanceTable = ({
  ranked,
  subFloor,
  medianRate,
}: {
  ranked: StorePerfRow[];
  subFloor: StorePerfRow[];
  medianRate: number | null;
}) => {
  const [sortField, setSortField] = useState<SortField>('conversionRate');
  const [asc, setAsc] = useState(true);

  const onSort = (field: SortField) => {
    if (field === sortField) {
      setAsc((prev) => !prev);
    } else {
      setSortField(field);
      setAsc(true);
    }
  };

  const sortedRanked = [...ranked].sort((a, b) => {
    const c = compare(a, b, sortField);
    return asc ? c : -c;
  });

  const isBelowMedian = (row: StorePerfRow): boolean =>
    medianRate != null &&
    row.conversionRate != null &&
    row.conversionRate < medianRate;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Band median conversion rate: <strong>{formatRate(medianRate)}</strong>.
        Rows below it are flagged.
      </Typography>
      <Table size="small" aria-label="stores by performance">
        <TableHead>
          <TableRow>
            {COLUMNS.map((col) => (
              <TableCell key={col.field} align={col.numeric ? 'right' : 'left'}>
                <TableSortLabel
                  active={sortField === col.field}
                  direction={sortField === col.field && !asc ? 'desc' : 'asc'}
                  onClick={() => onSort(col.field)}
                >
                  {col.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRanked.map((row) => (
            <StoreRow
              key={row.key}
              row={row}
              belowMedian={isBelowMedian(row)}
            />
          ))}

          {subFloor.length > 0 && (
            <>
              <TableRow>
                <TableCell colSpan={COLUMNS.length} sx={{ border: 0, pt: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Below the click floor (rate too noisy to rank) - unranked
                  </Typography>
                </TableCell>
              </TableRow>
              {subFloor.map((row) => (
                <StoreRow key={row.key} row={row} belowMedian={false} />
              ))}
            </>
          )}
        </TableBody>
      </Table>
    </Box>
  );
};
